import { useCallback, useMemo, useState } from 'react';

import { ZfBox, ZfDivider, ZfIcon, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import { ColDef } from 'ag-grid-enterprise';
import AGGridComponent from 'components/AgGrid/AGGridComponent';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { PortfolioDetails, ServiceModel } from 'pco-product-service-internal-consumer';
import { openModal } from '../../reducers/modalReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RowClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import { clearSelectedPortfolio, selectSelectedPortfolio, setSelectedPortfolio } from '../../reducers/portfolioReducer';
import { useTranslation } from 'react-i18next';
import { getStatusLabel } from '../PrivateBanking/privateBankingStatusLabels';

export interface PortfolioTableItemDetails extends PortfolioDetails {
  portfolioId: string;
  serviceModel?: ServiceModel;
  draftServiceModel?: ServiceModel;
}
export type PortfolioTableItemType = {
  title: string;
  portfolio: PortfolioTableItemDetails;
};

export type PortfolioRow = {
  portfolioId: string;
  portfolioNumber: string;
  linkedProduct: string;
  details: string;
  rubrik: string;
  portfolioType: string;
  createdAt: string;
  referenceCurrency: string;
  chargingCurrency: string;
  portfolioStatus: string;
};

export type CashAccountRow = NonNullable<PortfolioDetails['cashAccounts']>[number];

const PortfolioTableItem = ({ title, portfolio }: PortfolioTableItemType) => {
  const [showTable, setShowTable] = useState(true);
  const queryClient = useQueryClient();
  const { brUid } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedPortfolio = useSelector(selectSelectedPortfolio);

  const getColumnDefs = useMemo((): ColDef<PortfolioRow>[] => {
    return [
      {
        headerName: t('pb.portfoliosummary.header.portfolioid'),
        field: 'portfolioNumber',
        flex: 1,
      },
      {
        headerName: 'Details',
        field: 'details',
        flex: 1,
      },
      {
        headerName: t('pb.portfoliosummary.header.portfolioType'),
        field: 'portfolioType',
        flex: 1,
      },
      {
        headerName: t('pb.portfoliosummary.header.rubric'),
        field: 'rubrik',
        flex: 1,
      },
      { headerName: t('pb.portfoliosummary.header.referenceCurrency'), field: 'referenceCurrency', flex: 1 },
      { headerName: t('pb.portfoliosummary.header.chargingCurrency'), field: 'chargingCurrency', flex: 1 },
      {
        headerName: t('pb.portfoliosummary.header.portfolioStatus'),
        field: 'portfolioStatus',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<PortfolioRow>) => {
          if (params.value === null) return '-';
          return getStatusLabel(params.value);
        },
      },
      {
        headerName: t('pb.portfoliosummary.header.actions'),
        filter: false,
        flex: 1,
        cellRenderer: () => {
          if (!portfolio.canBeEdited) {
            return null;
          }

          return (
            <ZfStack
              direction="row"
              spacing="small"
              style={{ alignItems: 'center', justifyContent: 'space-between', height: '100%' }}
            >
              <span data-no-row-click style={{ lineHeight: 'normal' }}>
                <ZfTextButton
                  size="extra-small"
                  hierarchy="secondary"
                  style={{ lineHeight: 'normal' }}
                  onClick={() =>
                    dispatch(
                      openModal({
                        modalType: 'AddPortfolioDialog',
                        modalProps: {
                          brUid: brUid,
                          portfolio,
                          onProceed: () => queryClient.invalidateQueries({ queryKey: ['useGetPortfoliosList', brUid] }),
                        },
                      }),
                    )
                  }
                >
                  <ZfIcon slot="leading" name="add_circle" />
                  Edit
                </ZfTextButton>
              </span>
            </ZfStack>
          );
        },
      },
    ];
  }, [brUid, dispatch, portfolio, queryClient, t]);

  const cashAccountColumnDefs = useMemo((): ColDef<CashAccountRow>[] => {
    return [
      {
        headerName: 'Account ID',
        field: 'cashAccountNumber',
      },
      { headerName: 'Type', field: 'accountType' },
      { headerName: 'CCY', field: 'currency' },
      {
        headerName: 'Status',
        field: 'accountStatus',
        valueFormatter: (params: ValueFormatterParams<CashAccountRow>) => {
          if (params.value === null) return '-';
          return getStatusLabel(params.value);
        },
      },
    ];
  }, []);

  const cashAccountRows = (portfolio.cashAccounts ?? []).map((cashAccount) => ({
    ...cashAccount,
    rowId: cashAccount.cashAccountId ?? cashAccount.cashAccountNumber,
  })) as Record<string, unknown>[];

  const handleRowClick = useCallback(
    (event: RowClickedEvent) => {
      if ((event.event?.target as HTMLElement)?.closest('[data-no-row-click]')) return;
      const row = event.data as PortfolioTableItemDetails;
      if (!row) return;

      if (selectedPortfolio?.portfolioId === row.portfolioId) {
        dispatch(clearSelectedPortfolio());
        event.node.setSelected(false);
        return;
      }
      dispatch(setSelectedPortfolio(row));
    },
    [dispatch, selectedPortfolio],
  );

  const productName = useMemo(() => {
    const model = portfolio.draftServiceModel || portfolio.serviceModel;
    if (model) {
      return `${model.productDisplayName}`;
    }
    return;
  }, [portfolio.draftServiceModel, portfolio.serviceModel]);

  return (
    <>
      <ZfStack direction="row" spacing="small" style={{ alignItems: 'center' }}>
        <ZfBox>
          <ZfTypography variant="label-medium" color="emphasis" weight="bold">
            Portfolio {title}
          </ZfTypography>
          {productName && (
            <ZfBox>
              <ZfTypography variant="label-small">{productName}</ZfTypography>
            </ZfBox>
          )}
        </ZfBox>
        <ZfDivider style={{ flex: 1 }} />
        <ZfIcon
          name="arrow_forward_ios"
          size="medium"
          style={{
            transform: showTable ? 'rotate(-90deg)' : 'rotate(90deg)',
            transition: 'transform 0.3s ease',
            cursor: 'pointer',
          }}
          onClick={() => setShowTable(!showTable)}
        />{' '}
      </ZfStack>
      {showTable && (
        <>
          {' '}
          <div
            style={{
              width: '100%',
            }}
          >
            <ZfStack
              direction="row"
              style={{ justifyContent: 'flex-end', paddingBottom: 'var(--padding-top-md, 16px)' }}
            >
              <ZfTextButton
                hierarchy="secondary"
                onClick={() => {
                  dispatch(
                    openModal({
                      modalType: 'AddCashAccountDialog',
                      modalProps: {
                        portfolio,
                        onProceed: () => queryClient.invalidateQueries({ queryKey: ['useGetPortfoliosList', brUid] }),
                        brUid,
                      },
                    }),
                  );
                }}
              >
                <ZfIcon name="add_circle" size="medium" slot="leading" />
                Add Cash account
              </ZfTextButton>
            </ZfStack>
            <AGGridComponent
              id={`portfolio-id-${portfolio.portfolioId}`}
              rowKey={'portfolioId'}
              suppressClickEdit={false}
              onRowClick={handleRowClick}
              getColumnDefs={getColumnDefs}
              selectedItem={selectedPortfolio as unknown as Record<string, unknown>}
              items={[portfolio] as unknown as Record<string, unknown>[]}
              suppressRowHoverHighlight={false}
            />{' '}
          </div>
          <div
            style={{
              width: '100%',
            }}
          >
            {' '}
            <AGGridComponent
              id={'portfolio-id-cash-account'}
              disableSelection={true}
              containerStyle={{ maxHeight: 300, overflowY: 'auto', boxSizing: 'border-box' }}
              rowKey={'rowId'}
              getColumnDefs={cashAccountColumnDefs}
              items={cashAccountRows}
              rowSelection={{ mode: 'singleRow', enableClickSelection: false }}
              showPagination
            />{' '}
          </div>{' '}
        </>
      )}
    </>
  );
};

export default PortfolioTableItem;
